/**
 * SalidasPage — Lista plana de salidas programadas con DataTable + SlideOver.
 * Mismo patrón que PaquetesPage del Catálogo.
 *
 * Funciones:
 *   - Listar con búsqueda + filtros (paquete, estado, clima)
 *   - Crear/editar salida con SlideOver
 *   - Borrar con ConfirmDialog
 *   - Indicadores de ocupación (% cupos reservados)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, MenuItem, Chip, Stack,
  LinearProgress, Tooltip, ToggleButton, ToggleButtonGroup,
  Collapse, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import DataTable from '../common/DataTable';
import SlideOver from '../common/SlideOver';
import SuspenderSalidaDialog from './SuspenderSalidaDialog';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ConfirmDialog from '../common/ConfirmDialog';
import EmptyState from '../common/EmptyState';
import useToast from '../common/useToast';
import BotesSalidaDialog from './BotesSalidaDialog';
import SalidaCard from './shared/SalidaCard';

// Persistir preferencia del toggle Tarjetas/Tabla en localStorage
const VIEW_STORAGE_KEY = 'nara.pasadias.salidas.view';
function loadInitialView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'table' ? 'table' : 'cards';
  } catch { return 'cards'; }
}
function saveView(v) {
  try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch { /* no-op */ }
}

// Labels de días en español — mismo helper que ProximasPage
const DIAS_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
function _todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function _tomorrowLocal() {
  const d = new Date(); d.setDate(d.getDate()+1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function _yesterdayLocal() {
  const d = new Date(); d.setDate(d.getDate()-1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function _labelDia(yyyy_mm_dd) {
  if (!yyyy_mm_dd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyy_mm_dd)) return yyyy_mm_dd;
  if (yyyy_mm_dd === _todayLocal()) return 'Hoy';
  if (yyyy_mm_dd === _tomorrowLocal()) return 'Mañana';
  if (yyyy_mm_dd === _yesterdayLocal()) return 'Ayer';
  const d = new Date(yyyy_mm_dd + 'T00:00:00');
  const dia = DIAS_ES[d.getDay()];
  const mes = MESES_ES[d.getMonth()];
  return `${dia[0].toUpperCase()}${dia.slice(1)} ${d.getDate()} · ${mes} ${d.getFullYear()}`;
}

/**
 * Agrupa salidas por fecha (YYYY-MM-DD). Retorna array ordenado por fecha ASC
 * con KPIs pre-calculados por día: cantidad de salidas, capacidad total,
 * reservados totales y % ocupación agregado del día.
 */
function agruparSalidasPorDia(rows) {
  const buckets = new Map();
  for (const s of rows) {
    const key = String(s.fecha || '').slice(0, 10) || 'sin-fecha';
    if (!buckets.has(key)) {
      buckets.set(key, {
        dia: key,
        label: key === 'sin-fecha' ? 'Sin fecha' : _labelDia(key),
        salidas: [],
      });
    }
    buckets.get(key).salidas.push(s);
  }
  const arr = Array.from(buckets.values());
  arr.sort((a, b) => a.dia.localeCompare(b.dia));
  arr.forEach(g => {
    // Salidas del día ordenadas por hora de zarpe
    g.salidas.sort((a, b) =>
      String(a.hora_salida || '').localeCompare(String(b.hora_salida || ''))
    );
    const cap = g.salidas.reduce((s, x) => s + Number(x.capacidad_total || 0), 0);
    const res = g.salidas.reduce((s, x) => s + Number(x.asientos_reservados || 0), 0);
    g.kpis = {
      num: g.salidas.length,
      capacidad: cap,
      reservados: res,
      pctOcupacion: cap > 0 ? (res / cap) * 100 : 0,
    };
  });
  return arr;
}

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; }
  catch { return '1'; }
}
function authHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
function fmtFecha(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' }); }
  catch { return s; }
}

const ESTADO_COLORS = {
  programada: { bg: '#e0f2fe', color: '#075985', label: 'Programada' },
  activa:     { bg: '#dcfce7', color: '#166534', label: 'Activa' },
  completada: { bg: '#f1f5f9', color: '#475569', label: 'Completada' },
  cancelada:  { bg: '#fee2e2', color: '#991b1b', label: 'Cancelada' },
  suspendida: { bg: '#fef3c7', color: '#92400e', label: 'Suspendida' },
};

const CLIMA_OPTS = [
  { v: 'normal',    label: '☀️ Normal',     color: '#16a34a' },
  { v: 'lluvia',    label: '🌧️ Lluvia',     color: '#0ea5e9' },
  { v: 'tormenta',  label: '⛈️ Tormenta',   color: '#dc2626' },
  { v: 'cancelado', label: '🚫 Cancelado',  color: '#991b1b' },
];

const emptyForm = {
  paquete_id: '', punto_embarque_id: '', fecha: '', hora_salida: '08:00',
  hora_regreso: '15:00', capacidad_total: '', estado: 'programada',
  estado_clima: 'normal', precio_adulto_override: '', precio_nino_override: '',
  notas: '',
};

export default function SalidasPage() {
  const empresaId = getEmpresaId();
  const { refreshCounts } = useOutletContext() || {};
  const { toast, ToastContainer } = useToast();

  const [salidas, setSalidas] = useState([]);
  const [paquetes, setPaquetes] = useState([]);
  const [puntosEmbarque, setPuntosEmbarque] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPaquete, setFilterPaquete] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterClima, setFilterClima] = useState('');
  // Rango de fechas por default: últimos 30 días + futuras. Presets:
  //   hoy | semana | mes (default) | trimestre | todas
  // Cambio del toggle 'soloFuturas' (booleano) al rango para no ocultar el
  // histórico y permitir buscar canceladas/completadas anteriores.
  const [rangoFecha, setRangoFecha] = useState('mes');

  const [slideOpen, setSlideOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  // Los botes no se editan en el formulario de la salida: la relación es de
  // muchos a muchos y vive aparte. Se abren desde la columna de botes.
  const [botesSalida, setBotesSalida] = useState(null);
  const [embarcaciones, setEmbarcaciones] = useState([]);
  const [suspenderSalida, setSuspenderSalida] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Toggle Tarjetas/Tabla — mismo patrón que HoyPage/ProximasPage
  const [view, setView] = useState(loadInitialView);
  const handleView = (_e, v) => {
    if (!v) return;
    setView(v);
    saveView(v);
  };

  // Días expandidos en modo Tarjetas (Set de 'YYYY-MM-DD')
  const [diasExpandidos, setDiasExpandidos] = useState(() => new Set());
  const toggleDia = (dia) => {
    setDiasExpandidos(prev => {
      const s = new Set(prev);
      if (s.has(dia)) s.delete(dia); else s.add(dia);
      return s;
    });
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, pRes, peRes, eRes] = await Promise.all([
        fetch(`${API_BASE}/turismo/salidas?empresa_id=${empresaId}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/turismo/puntos-embarque?empresa_id=${empresaId}`, { headers: authHeaders() }),
        fetch(`${API_BASE}/turismo/embarcaciones?empresa_id=${empresaId}&solo_activos=true`, { headers: authHeaders() }),
      ]);
      const sData = await sRes.json();
      setSalidas(Array.isArray(sData) ? sData : (sData.salidas || []));
      setPaquetes(await pRes.json());
      setPuntosEmbarque(await peRes.json());
      const eData = await eRes.json().catch(() => []);
      setEmbarcaciones(Array.isArray(eData) ? eData : (eData.embarcaciones || []));
    } catch (e) { console.error(e); toast.error('Error cargando salidas'); }
    setLoading(false);
  }, [empresaId]); // eslint-disable-line

  useEffect(() => { cargar(); }, [cargar]);

  // Rango de fechas aplicado según el preset. Nulo = sin límite hacia atrás.
  // Todas las opciones incluyen SIEMPRE las futuras — el operador quiere ver
  // lo próximo + un periodo hacia atrás para comparar y auditar.
  const rangoDesde = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const d = new Date(hoy);
    if (rangoFecha === 'hoy')       { return hoy; }
    if (rangoFecha === 'semana')    { d.setDate(d.getDate() - 7);  return d; }
    if (rangoFecha === 'mes')       { d.setDate(d.getDate() - 30); return d; }
    if (rangoFecha === 'trimestre') { d.setDate(d.getDate() - 90); return d; }
    return null;   // 'todas'
  }, [rangoFecha]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return salidas.filter(s => {
      if (rangoDesde && s.fecha) {
        const f = new Date(s.fecha);
        if (f < rangoDesde) return false;
      }
      if (filterPaquete && s.paquete_id !== parseInt(filterPaquete)) return false;
      if (filterEstado && s.estado !== filterEstado) return false;
      if (filterClima && (s.estado_clima || 'normal') !== filterClima) return false;
      if (q) {
        const txt = `${s.paquete_nombre || ''} ${s.codigo || ''} ${s.notas || ''}`.toLowerCase();
        if (!txt.includes(q)) return false;
      }
      return true;
    });
  }, [salidas, search, filterPaquete, filterEstado, filterClima, rangoDesde]);

  // Agrupación por día (ASC — más antiguas primero). Solo se usa en modo Tarjetas
  const grupos = useMemo(() => agruparSalidasPorDia(filteredRows), [filteredRows]);

  // Al cambiar el conjunto de días, expandir por default los primeros 5
  // (evita muro de contenido cuando hay muchos días en el rango). Si hay
  // búsqueda activa, expandir todos para que se vean los hits.
  useEffect(() => {
    if (grupos.length === 0) {
      setDiasExpandidos(new Set());
    } else if (search.trim()) {
      setDiasExpandidos(new Set(grupos.map(g => g.dia)));
    } else {
      // Preferir días futuros/hoy — están al final del array (orden ASC).
      // Si hay muchos días históricos + futuros, expandir del "hoy" hacia
      // adelante.
      const hoy = _todayLocal();
      const desdeHoyIdx = grupos.findIndex(g => g.dia >= hoy);
      const start = desdeHoyIdx === -1 ? Math.max(grupos.length - 5, 0) : desdeHoyIdx;
      setDiasExpandidos(new Set(grupos.slice(start, start + 5).map(g => g.dia)));
    }
  }, [grupos.map(g => g.dia).join('|'), search]); // eslint-disable-line

  const expandirTodos = () => setDiasExpandidos(new Set(grupos.map(g => g.dia)));
  const colapsarTodos = () => setDiasExpandidos(new Set());
  const todosExpandidos = grupos.length > 0 && diasExpandidos.size === grupos.length;

  // Conteos por estado en las filas del rango elegido — para mostrarlos en los chips
  const conteosPorEstado = useMemo(() => {
    const c = { total: 0, programada: 0, activa: 0, completada: 0, cancelada: 0, suspendida: 0 };
    salidas.forEach(s => {
      if (rangoDesde && s.fecha) {
        const f = new Date(s.fecha);
        if (f < rangoDesde) return;
      }
      c.total++;
      if (c[s.estado] !== undefined) c[s.estado]++;
    });
    return c;
  }, [salidas, rangoDesde]);

  const handleNew = () => {
    setEditItem(null);
    setForm({ ...emptyForm,
      paquete_id: paquetes[0]?.id ? String(paquetes[0].id) : '',
      punto_embarque_id: puntosEmbarque[0]?.id ? String(puntosEmbarque[0].id) : '',
      capacidad_total: paquetes[0]?.capacidad_maxima ? String(paquetes[0].capacidad_maxima) : '50',
    });
    setSlideOpen(true);
  };

  const handleEdit = (row) => {
    setEditItem(row);
    setForm({
      paquete_id: String(row.paquete_id || ''),
      punto_embarque_id: String(row.punto_embarque_id || ''),
      fecha: row.fecha ? row.fecha.split('T')[0] : '',
      hora_salida: row.hora_salida || '08:00',
      hora_regreso: row.hora_regreso || '15:00',
      capacidad_total: String(row.capacidad_override ?? row.capacidad_total ?? ''),
      estado: row.estado || 'programada',
      estado_clima: row.estado_clima || 'normal',
      precio_adulto_override: row.precio_adulto_override ?? '',
      precio_nino_override: row.precio_nino_override ?? '',
      notas: row.notas || '',
    });
    setSlideOpen(true);
  };

  const handleSave = async () => {
    if (!form.paquete_id || !form.fecha) {
      toast.error('Paquete y fecha son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const body = {
        empresa_id: empresaId,
        paquete_id: parseInt(form.paquete_id),
        punto_embarque_id: form.punto_embarque_id ? parseInt(form.punto_embarque_id) : null,
        fecha: form.fecha,
        hora_salida: form.hora_salida || null,
        hora_regreso: form.hora_regreso || null,
        capacidad_override: form.capacidad_total ? parseInt(form.capacidad_total) : null,
        estado: form.estado,
        estado_clima: form.estado_clima,
        precio_adulto_override: form.precio_adulto_override !== '' ? Number(form.precio_adulto_override) : null,
        precio_nino_override: form.precio_nino_override !== '' ? Number(form.precio_nino_override) : null,
        notas: form.notas || null,
      };
      const url = editItem ? `${API_BASE}/turismo/salidas/${editItem.id}` : `${API_BASE}/turismo/salidas`;
      const method = editItem ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success(editItem ? 'Salida actualizada' : 'Salida creada');
      setSlideOpen(false);
      cargar();
      refreshCounts?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await fetch(`${API_BASE}/turismo/salidas/${deleteTarget.id}`,
        { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      toast.success('Salida eliminada');
      setDeleteTarget(null);
      cargar();
      refreshCounts?.();
    } catch (e) { toast.error(e.message); }
  };

  const columns = [
    {
      id: 'fecha', label: 'Fecha', sortable: true,
      sortValue: (r) => r.fecha,
      render: (r) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{fmtFecha(r.fecha)}</Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }}>
            {r.hora_salida?.slice(0, 5) || '—'} → {r.hora_regreso?.slice(0, 5) || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'paquete', label: 'Paquete', sortable: true,
      sortValue: (r) => r.paquete_nombre || '',
      render: (r) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{r.paquete_nombre || '—'}</Typography>
          {r.punto_embarque_nombre && (
            <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>⚓ {r.punto_embarque_nombre}</Typography>
          )}
          {r.promo_aplicada && (
            <Tooltip title={
              <Box sx={{ p: 0.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.5 }}>{r.promo_aplicada.nombre}</Typography>
                <Typography sx={{ fontSize: 10 }}>
                  Rango: {r.promo_aplicada.fecha_inicio} → {r.promo_aplicada.fecha_fin}
                </Typography>
                <Typography sx={{ fontSize: 10 }}>
                  Neto adulto: ${Number(r.promo_aplicada.precio_neto_adulto).toLocaleString('es-CO')}
                  {r.promo_aplicada.precio_neto_nino != null && <> · niño ${Number(r.promo_aplicada.precio_neto_nino).toLocaleString('es-CO')}</>}
                </Typography>
              </Box>
            }>
              <Chip size="small" label={`🏷️ ${r.promo_aplicada.nombre}`}
                sx={{ mt: 0.5, bgcolor: '#fef3c7', color: '#92400e', fontSize: 10, height: 18, fontWeight: 700 }} />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      id: 'ocupacion', label: 'Ocupación', sortable: true,
      sortValue: (r) => (r.asientos_reservados || 0) / Math.max(r.capacidad_total || 1, 1),
      render: (r) => {
        const cap = r.capacidad_total || 0;
        const res = r.asientos_reservados || 0;
        const pct = cap > 0 ? (res / cap) * 100 : 0;
        const color = pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#16a34a';
        return (
          <Box sx={{ minWidth: 100 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color }}>
              {res}/{cap} ({pct.toFixed(0)}%)
            </Typography>
            <LinearProgress variant="determinate" value={Math.min(pct, 100)}
              sx={{ height: 4, borderRadius: 2, mt: 0.5,
                     bgcolor: '#f1f5f9',
                     '& .MuiLinearProgress-bar': { bgcolor: color } }} />
          </Box>
        );
      },
    },
    {
      id: 'botes', label: 'Botes', sortable: true,
      // El backend devuelve la relación M:N en `embarcaciones_asignadas`. También
      // existe el legacy `embarcacion_id`/`embarcacion_nombre` (bote único). El
      // fallback cubre salidas viejas que solo tienen el legacy sin la M:N.
      sortValue: (r) => (r.embarcaciones_asignadas || []).length || (r.embarcacion_id ? 1 : 0),
      render: (r) => {
        const asignadas = r.embarcaciones_asignadas || [];
        const n = asignadas.length || (r.embarcacion_id ? 1 : 0);
        if (n === 0) {
          return <Chip size="small" label="Sin botes" clickable
                   onClick={(e) => { e.stopPropagation(); setBotesSalida(r); }}
                   title="Asignar botes a esta salida"
                   sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 10, height: 20 }} />;
        }
        // Mostrar los nombres para que el operador vea de inmediato el bote asignado
        const nombres = asignadas.length
          ? asignadas.map(ea => ea.embarcacion_nombre).filter(Boolean).join(' · ')
          : (r.embarcacion_nombre || '—');
        return (
          <Chip size="small" clickable
            onClick={(e) => { e.stopPropagation(); setBotesSalida(r); }}
            title="Ver o cambiar los botes de esta salida"
            label={nombres || `${n} bote${n > 1 ? 's' : ''}`}
            sx={{ bgcolor: '#e0f2fe', color: '#075985', fontSize: 10, height: 20, fontWeight: 600 }} />
        );
      },
    },
    {
      id: 'estado', label: 'Estado',
      render: (r) => {
        const c = ESTADO_COLORS[r.estado] || ESTADO_COLORS.programada;
        return <Chip size="small" label={c.label} sx={{ bgcolor: c.bg, color: c.color, fontSize: 10, height: 20, fontWeight: 700 }} />;
      },
    },
    {
      id: 'clima', label: 'Clima',
      render: (r) => {
        const c = CLIMA_OPTS.find(x => x.v === (r.estado_clima || 'normal')) || CLIMA_OPTS[0];
        return <Typography sx={{ fontSize: 11 }}>{c.label}</Typography>;
      },
    },
    {
      id: 'acciones', label: '', align: 'right',
      render: (r) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button size="small" onClick={(e) => { e.stopPropagation(); setBotesSalida(r); }}
            startIcon={<DirectionsBoatIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', minWidth: 'auto', fontSize: 11 }}>Botes</Button>
          <Button size="small" onClick={(e) => { e.stopPropagation(); handleEdit(r); }}
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', minWidth: 'auto', fontSize: 11 }}>Editar</Button>
          <Button size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
            sx={{ minWidth: 'auto' }}>
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Salidas programadas</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Cada salida es un pasadía operativo: fecha + paquete + botes + cupos.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew} disableElevation
          sx={{ textTransform: 'none', fontWeight: 700,
                 background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
          Nueva salida
        </Button>
      </Box>

      {/* Filtros — línea 1: búsqueda y selectores */}
      <Box sx={{ display: 'grid',
        // xs: apila; sm/md: 2 cols; lg: fila única
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(2, 1fr)',
          lg: '2fr 1fr 1fr 1fr',
        }, gap: 1, mb: 1 }}>
        <TextField size="small" placeholder="Buscar paquete, código…"
          value={search} onChange={e => setSearch(e.target.value)} />
        <TextField size="small" select label="Paquete" value={filterPaquete}
          onChange={e => setFilterPaquete(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {paquetes.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Rango" value={rangoFecha}
          onChange={e => setRangoFecha(e.target.value)}>
          <MenuItem value="hoy">Hoy y futuras</MenuItem>
          <MenuItem value="semana">Últimos 7 días + futuras</MenuItem>
          <MenuItem value="mes">Últimos 30 días + futuras</MenuItem>
          <MenuItem value="trimestre">Últimos 90 días + futuras</MenuItem>
          <MenuItem value="todas">Todas (histórico completo)</MenuItem>
        </TextField>
        <TextField size="small" select label="Clima" value={filterClima}
          onChange={e => setFilterClima(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {CLIMA_OPTS.map(c => <MenuItem key={c.v} value={c.v}>{c.label}</MenuItem>)}
        </TextField>
      </Box>

      {/* Chips rápidos de estado + toggle Tarjetas/Tabla + expandir todos */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small" value={view} exclusive onChange={handleView}
          sx={{
            bgcolor: '#f8fafc',
            mr: 0.75,
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: 12,
              px: 1.25, py: 0.6, color: '#475569',
              border: '1px solid #e2e8f0',
              '&.Mui-selected': {
                bgcolor: '#fff', color: '#0f172a',
                boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
              },
            },
          }}
        >
          <ToggleButton value="cards"><ViewModuleIcon sx={{ fontSize: 15, mr: 0.5 }} /> Tarjetas</ToggleButton>
          <ToggleButton value="table"><ViewListIcon sx={{ fontSize: 15, mr: 0.5 }} /> Tabla</ToggleButton>
        </ToggleButtonGroup>

        <Chip size="small" label={`Todas (${conteosPorEstado.total})`}
          onClick={() => setFilterEstado('')}
          variant={filterEstado === '' ? 'filled' : 'outlined'}
          sx={{
            fontSize: 11, fontWeight: 700,
            bgcolor: filterEstado === '' ? '#0d9488' : 'transparent',
            color: filterEstado === '' ? '#fff' : '#334155',
            borderColor: '#cbd5e1',
            '&:hover': { bgcolor: filterEstado === '' ? '#0f766e' : '#f1f5f9' },
          }} />
        {[
          { k: 'programada', c: ESTADO_COLORS.programada },
          { k: 'activa',     c: ESTADO_COLORS.activa },
          { k: 'completada', c: ESTADO_COLORS.completada },
          { k: 'suspendida', c: ESTADO_COLORS.suspendida },
          { k: 'cancelada',  c: ESTADO_COLORS.cancelada },
        ].map(({ k, c }) => {
          const sel = filterEstado === k;
          const n = conteosPorEstado[k] || 0;
          return (
            <Chip key={k} size="small" label={`${c.label} (${n})`}
              onClick={() => setFilterEstado(sel ? '' : k)}
              variant={sel ? 'filled' : 'outlined'}
              sx={{
                fontSize: 11, fontWeight: 700,
                bgcolor: sel ? c.color : 'transparent',
                color: sel ? '#fff' : c.color,
                borderColor: c.color,
                opacity: n === 0 ? 0.4 : 1,
                '&:hover': { bgcolor: sel ? c.color : `${c.bg}` },
              }} />
          );
        })}
      </Box>

      {/* Botón expandir/colapsar todos — visible en ambos modos si hay >1 día */}
      {grupos.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button size="small"
            startIcon={todosExpandidos
              ? <UnfoldLessIcon sx={{ fontSize: 14 }} />
              : <UnfoldMoreIcon sx={{ fontSize: 14 }} />}
            onClick={() => (todosExpandidos ? colapsarTodos() : expandirTodos())}
            sx={{ textTransform: 'none', fontSize: 11, color: '#0891b2' }}>
            {todosExpandidos ? 'Colapsar todos' : 'Expandir todos'}
          </Button>
        </Box>
      )}

      {/* Contenido: SIEMPRE agrupado por día con headers colapsables.
          Adentro cambia solo el layout: SalidaCards (view='cards') o
          DataTable filtrada al día (view='table'). Mismo patrón visual
          del header — coherencia total entre modos. */}
      {filteredRows.length === 0 && !loading ? (
        <EmptyState
          icon={<EventNoteIcon sx={{ fontSize: 56, color: '#cbd5e1' }} />}
          title="Sin salidas programadas"
          message={search || filterPaquete || filterEstado
            ? "No hay salidas que coincidan con los filtros."
            : "Crea tu primera salida o usa 'Generar masivas' para programar varias fechas a la vez."}
          actionLabel="Nueva salida"
          onAction={handleNew}
        />
      ) : (
        <Stack spacing={1}>
          {grupos.map(g => {
            const expandido = diasExpandidos.has(g.dia);
            const pctColorDia = g.kpis.pctOcupacion >= 90 ? '#dc2626'
              : g.kpis.pctOcupacion >= 70 ? '#f59e0b' : '#16a34a';
            return (
              <Box key={g.dia} sx={{
                bgcolor: '#fff', border: '1px solid #e2e8f0',
                borderRadius: 2, overflow: 'hidden',
              }}>
                <Box onClick={() => toggleDia(g.dia)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'auto 1fr auto', sm: 'auto 1fr auto auto auto' },
                    alignItems: 'center', gap: { xs: 1, sm: 2 },
                    p: 1.25, cursor: 'pointer', userSelect: 'none',
                    bgcolor: expandido ? '#f0f9ff' : '#f8fafc',
                    borderBottom: expandido ? '1px solid #bae6fd' : 'none',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: expandido ? '#e0f2fe' : '#eef2f6' },
                  }}>
                  <IconButton size="small" sx={{
                    transition: 'transform 0.18s',
                    transform: expandido ? 'rotate(0deg)' : 'rotate(-90deg)',
                    color: '#0891b2',
                  }}>
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: { xs: 13, sm: 14 }, fontWeight: 800, color: '#0f172a',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {g.label}
                    </Typography>
                    <Typography sx={{
                      fontSize: 11, color: '#64748b',
                      display: { xs: 'block', sm: 'none' },
                    }}>
                      {g.kpis.num} {g.kpis.num === 1 ? 'salida' : 'salidas'} · {g.kpis.reservados}/{g.kpis.capacidad} cupos
                    </Typography>
                  </Box>
                  <Chip size="small"
                    label={`${g.kpis.num} ${g.kpis.num === 1 ? 'salida' : 'salidas'}`}
                    sx={{
                      bgcolor: '#e0f2fe', color: '#0369a1',
                      fontWeight: 700, fontSize: 11, height: 22,
                      display: { xs: 'none', sm: 'inline-flex' },
                    }} />
                  <Chip size="small"
                    label={`${g.kpis.reservados}/${g.kpis.capacidad} cupos`}
                    sx={{
                      bgcolor: '#ccfbf1', color: '#0f766e',
                      fontWeight: 700, fontSize: 11, height: 22,
                      display: { xs: 'none', sm: 'inline-flex' },
                    }} />
                  <Typography sx={{
                    fontSize: 13, fontWeight: 800, color: pctColorDia,
                    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                    fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                  }}>
                    {g.kpis.pctOcupacion.toFixed(0)}%
                  </Typography>
                </Box>

                <Collapse in={expandido} timeout={180} unmountOnExit>
                  {view === 'cards' ? (
                    <Box sx={{
                      p: 1.25,
                      // Grid adaptativo: cada tarjeta pide al menos 280px de ancho.
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    }}>
                      {g.salidas.map(s => (
                        <SalidaCard key={s.id}
                          salida={s}
                          onEdit={() => handleEdit(s)}
                          onSuspender={() => setSuspenderSalida(s)}
                          onDelete={() => setDeleteTarget(s)}
                        />
                      ))}
                    </Box>
                  ) : (
                    // Modo Tabla: mismo DataTable pero solo con las salidas
                    // del día. La columna "Fecha" es redundante (ya está en
                    // el header), pero se mantiene porque muestra hora de
                    // zarpe + regreso, información valiosa por fila.
                    <Box sx={{ p: 1, pt: 0.5, overflowX: 'auto' }}>
                      <DataTable
                        columns={columns}
                        rows={g.salidas}
                        loading={false}
                        rowKey={(r) => r.id}
                        defaultSortBy="fecha"
                        defaultSortDir="asc"
                        onRowClick={(r) => handleEdit(r)}
                      />
                    </Box>
                  )}
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* SlideOver edición */}
      <SlideOver
        open={slideOpen}
        onClose={() => setSlideOpen(false)}
        title={editItem ? `Editar salida #${editItem.id}` : 'Nueva salida'}
        subtitle={editItem ? fmtFecha(editItem.fecha) : 'Programa un pasadía'}
        footer={
          <>
            <Button onClick={() => setSlideOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>Cancelar</Button>
            {editItem && !['suspendida', 'cancelada'].includes(editItem.estado) && (
              <Button onClick={() => { setSuspenderSalida(editItem); setSlideOpen(false); }}
                startIcon={<EventBusyIcon />}
                sx={{ textTransform: 'none', color: '#dc2626', fontWeight: 700 }}>
                Suspender salida
              </Button>
            )}
            <Button variant="contained" onClick={handleSave} disabled={saving} disableElevation
              sx={{ textTransform: 'none', fontWeight: 700,
                     background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }>
        <Stack spacing={1.5}>
          <TextField size="small" select label="Paquete *" value={form.paquete_id}
            onChange={e => setForm({ ...form, paquete_id: e.target.value })}>
            {paquetes.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Punto de embarque" value={form.punto_embarque_id}
            onChange={e => setForm({ ...form, punto_embarque_id: e.target.value })}>
            <MenuItem value="">— Ninguno —</MenuItem>
            {puntosEmbarque.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
          </TextField>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
            <TextField size="small" type="date" label="Fecha *" value={form.fecha}
              onChange={e => setForm({ ...form, fecha: e.target.value })}
              InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="time" label="Hora salida" value={form.hora_salida}
              onChange={e => setForm({ ...form, hora_salida: e.target.value })}
              InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="time" label="Hora regreso" value={form.hora_regreso}
              onChange={e => setForm({ ...form, hora_regreso: e.target.value })}
              InputLabelProps={{ shrink: true }} />
          </Box>
          <TextField size="small" type="number" label="Capacidad total" value={form.capacidad_total}
            onChange={e => setForm({ ...form, capacidad_total: e.target.value })}
            helperText="Sobrescribe la suma de botes asignados. Déjalo vacío para que use la suma automática." />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField size="small" select label="Estado" value={form.estado}
              onChange={e => setForm({ ...form, estado: e.target.value })}>
              {Object.entries(ESTADO_COLORS).map(([k, c]) => <MenuItem key={k} value={k}>{c.label}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Clima" value={form.estado_clima}
              onChange={e => setForm({ ...form, estado_clima: e.target.value })}>
              {CLIMA_OPTS.map(c => <MenuItem key={c.v} value={c.v}>{c.label}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <TextField size="small" type="number" label="Precio adulto (override)" value={form.precio_adulto_override}
              onChange={e => setForm({ ...form, precio_adulto_override: e.target.value })}
              helperText="Vacío = usa precio del paquete" />
            <TextField size="small" type="number" label="Precio niño (override)" value={form.precio_nino_override}
              onChange={e => setForm({ ...form, precio_nino_override: e.target.value })} />
          </Box>
          <TextField size="small" multiline minRows={2} label="Notas" value={form.notas}
            onChange={e => setForm({ ...form, notas: e.target.value })} />
        </Stack>
      </SlideOver>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="¿Eliminar salida?"
        message={deleteTarget ? `${fmtFecha(deleteTarget.fecha)} · ${deleteTarget.paquete_nombre || '—'}. Esta acción no se puede deshacer.` : ''}
        confirmText="Eliminar"
        severity="danger"
      />

      {suspenderSalida && (
        <SuspenderSalidaDialog
          salida={suspenderSalida}
          onClose={() => setSuspenderSalida(null)}
          onSuspendida={() => { cargar(); refreshCounts?.(); }}
        />
      )}

      {botesSalida && (
        <BotesSalidaDialog
          salida={botesSalida}
          embarcaciones={embarcaciones}
          onClose={() => setBotesSalida(null)}
          onCambio={cargar}
        />
      )}

      <ToastContainer />
    </Box>
  );
}
